import BadgeDataType from "@/components/ui/badge-data-type";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { DATA_TYPE_VALUES } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  ATTRIBUTE_ID,
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDataType
} from "@/types/dataset";

import { twMerge } from "tailwind-merge";

/** A single selectable option; `dataType` renders a data-type badge. */
export type ColorBasedOption = {
  label: string;
  value: string;
  dataType?: DataSchemaType;
};

interface ColorBasedSelectProps {
  title?: string;
  titleClassName?: string;
  selectContentClassName?: string;
  selectTriggerClassName?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  /**
   * Attribute list rendered as options (each with a data-type badge). Used by
   * the "Column Option" select. Ignored when `options` is provided.
   */
  categories?: DatasetAttributeSchema[];
  /** Explicit option list (e.g. Color Scale methods). Takes precedence. */
  options?: ColorBasedOption[];
  placeholder?: string;
  /** Trigger test id — override so multiple selects can coexist in one panel. */
  triggerTestId?: string;
  disabled?: boolean;
}

const ColorBasedSelect = ({
  value,
  title,
  titleClassName,
  selectContentClassName,
  selectTriggerClassName,
  onValueChange,
  categories,
  options,
  placeholder = "Select a color scale",
  triggerTestId = "color-based-select-trigger",
  disabled = false
}: ColorBasedSelectProps) => {
  const { isCustomMapVisualizationLegendEnabled } = useAppFeatures();

  // Default label depends on the Custom Map Visualization Legend feature:
  // when enabled this select is the "Column Option"; otherwise it keeps the
  // legacy "Color Based On" label. An explicit `title` prop always wins.
  const resolvedTitle =
    title ??
    (isCustomMapVisualizationLegendEnabled
      ? "Column Option"
      : "Color Based On");

  // Either render the explicit `options`, or derive them from `categories`
  // (filtering out reference-id and geometry attributes, with a badge).
  const items: ColorBasedOption[] =
    options ??
    (categories ?? [])
      .filter(
        (category) =>
          !(category.attribute_name in ATTRIBUTE_ID) &&
          category.data_type !==
            DATA_TYPE_VALUES[DatasetDataType.TYPE_GEOMETRY] &&
          category.data_type !== DataSchemaType.GEOMETRY
      )
      .map((category) => ({
        label: category.attribute_name,
        value: category.attribute_name,
        dataType: category.data_type
      }));

  return (
    <div className="w-full space-y-2">
      <p className={twMerge("text-grey text-xs mb-1", titleClassName)}>
        {resolvedTitle}
      </p>
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger
          className={twMerge(
            "rounded-xl h-auto w-full [&>span]:w-full",
            selectTriggerClassName
          )}
          data-testid={triggerTestId}
        >
          <SelectValue placeholder={placeholder} className="w-full" />
        </SelectTrigger>
        <SelectContent className={selectContentClassName}>
          <SelectGroup className="w-full">
            {items.map((item) => (
              <SelectItem
                key={item.value}
                value={item.value}
                data-testid={`color-based-select-option-${item.value}`}
                className="flex items-center gap-2 py-1 px-3 w-full [&>span]:w-full hover:!bg-primary-teal/50 focus:!bg-primary-teal/50"
              >
                <div className="flex flex-row items-center gap-2 justify-start w-full rounded-sm overflow-hidden text-txtHeading text-xs">
                  {item.dataType && <BadgeDataType type={item.dataType} />}
                  <p className="w-3/4 text-left">{item.label}</p>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};

export default ColorBasedSelect;
